import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft, Loader2, Plus, Sparkles, Check, Trash2, ExternalLink,
} from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { supabase } from '../../lib/supabaseClient'
import { useAuth } from '../../contexts/AuthContext'
import { cn } from '../../lib/utils'

// /wishlist — want-to-buy items tied to finance. One tap asks the
// wishlist-advice function for a buy-now / wait / skip verdict per
// item, computed from the actual balance, surplus and targets.

const VERDICT_TONE = {
  now: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  wait: 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400',
  skip: 'border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400',
}

export function WishlistPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [busy, setBusy] = useState(false)
  const [advising, setAdvising] = useState(false)
  const [advice, setAdvice] = useState({}) // id -> {verdict, reason}

  const refetch = useCallback(async () => {
    if (!user) return
    const { data } = await supabase
      .from('wishlist_items')
      .select('*')
      .eq('user_id', user.id)
      .neq('status', 'dropped')
      .order('status')
      .order('priority')
      .order('created_at', { ascending: false })
    setItems(data || [])
  }, [user])

  useEffect(() => {
    if (!user) { setLoading(false); return }
    refetch().then(() => setLoading(false))
  }, [user, refetch])

  const add = async (values) => {
    setBusy(true)
    const { error } = await supabase.from('wishlist_items').insert({
      user_id: user.id,
      name: values.name.trim(),
      price: values.price ? Number(values.price) : null,
      url: (values.url || '').trim() || null,
      priority: Number(values.priority) || 2,
      notes: (values.notes || '').trim() || null,
    })
    setBusy(false)
    if (!error) { setAdding(false); refetch() }
    return !error
  }

  const setStatus = async (id, status) => {
    await supabase.from('wishlist_items').update({ status, updated_at: new Date().toISOString() }).eq('id', id)
    refetch()
  }

  const getAdvice = async () => {
    if (advising) return
    setAdvising(true)
    const { data, error } = await supabase.functions.invoke('wishlist-advice', { body: {} })
    setAdvising(false)
    if (error || !data?.advice) return
    const map = {}
    for (const a of data.advice) map[a.id] = a
    setAdvice(map)
  }

  const wanted = items.filter((i) => i.status === 'wanted')
  const bought = items.filter((i) => i.status === 'bought')

  return (
    <div className="space-y-4 content-fade pb-8">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => navigate('/')}
          aria-label={t('settings.back')}
          className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
        >
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <h1 className="flex-1 min-w-0 text-lg font-bold text-foreground truncate" dir="auto">
          {t('wishlist.title')}
        </h1>
        {!adding && (
          <Button size="sm" onClick={() => setAdding(true)}>
            <Plus className="w-4 h-4 me-1" />
            {t('wishlist.add')}
          </Button>
        )}
      </div>

      {adding && <ItemForm onSave={add} onCancel={() => setAdding(false)} busy={busy} t={t} />}

      {loading && (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
        </div>
      )}

      {!loading && wanted.length === 0 && !adding && (
        <p className="text-sm text-muted-foreground text-center py-8" dir="auto">{t('wishlist.empty')}</p>
      )}

      {wanted.length > 0 && (
        <>
          <Button onClick={getAdvice} disabled={advising} variant="outline" className="w-full">
            {advising ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 me-1.5" />{t('wishlist.advise')}</>}
          </Button>
          <Card>
            <CardContent className="p-0 divide-y divide-border/60">
              {wanted.map((item) => (
                <ItemRow
                  key={item.id}
                  item={item}
                  advice={advice[item.id]}
                  onBought={() => setStatus(item.id, 'bought')}
                  onDrop={() => setStatus(item.id, 'dropped')}
                  t={t}
                />
              ))}
            </CardContent>
          </Card>
        </>
      )}

      {bought.length > 0 && (
        <details>
          <summary className="text-xs text-muted-foreground cursor-pointer px-1" dir="auto">
            {t('wishlist.boughtList', { count: bought.length })}
          </summary>
          <Card className="mt-2">
            <CardContent className="p-0 divide-y divide-border/60">
              {bought.map((item) => (
                <div key={item.id} className="p-3 flex items-center gap-2 text-sm text-muted-foreground" dir="auto">
                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span className="flex-1 min-w-0 truncate line-through">{item.name}</span>
                  {item.price != null && <span dir="ltr">{Number(item.price).toLocaleString('en-US')} SAR</span>}
                </div>
              ))}
            </CardContent>
          </Card>
        </details>
      )}
    </div>
  )
}

function ItemRow({ item, advice, onBought, onDrop, t }) {
  const [confirming, setConfirming] = useState(false)
  return (
    <div className="p-3 space-y-1.5">
      <div className="flex items-center gap-2">
        <span className={cn(
          'w-1.5 h-6 rounded-full flex-shrink-0',
          item.priority === 1 ? 'bg-red-500' : item.priority === 2 ? 'bg-amber-500' : 'bg-muted-foreground/40',
        )} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground truncate" dir="auto">{item.name}</p>
          {item.price != null && (
            <p className="text-xs text-muted-foreground" dir="ltr">{Number(item.price).toLocaleString('en-US')} SAR</p>
          )}
        </div>
        {item.url && (
          <a
            href={item.url}
            target="_blank"
            rel="noreferrer"
            aria-label={t('wishlist.open')}
            className="p-1.5 rounded-md hover:bg-accent text-muted-foreground"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
        <button
          type="button"
          onClick={onBought}
          aria-label={t('wishlist.bought')}
          title={t('wishlist.bought')}
          className="p-1.5 rounded-md hover:bg-accent text-emerald-500"
        >
          <Check className="w-4 h-4" />
        </button>
        {confirming ? (
          <Button size="sm" variant="destructive" onClick={onDrop} onBlur={() => setConfirming(false)}>
            {t('wishlist.confirm')}
          </Button>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            aria-label={t('common.delete')}
            className="p-1.5 rounded-md hover:bg-accent text-muted-foreground"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      {item.notes && <p className="text-xs text-muted-foreground" dir="auto">{item.notes}</p>}
      {advice && (
        <div className={cn('rounded-md border px-2.5 py-2 text-xs', VERDICT_TONE[advice.verdict] || VERDICT_TONE.wait)} dir="auto">
          <b>{t(`wishlist.verdict.${advice.verdict}`, advice.verdict)}</b> — {advice.reason}
        </div>
      )}
    </div>
  )
}

function ItemForm({ onSave, onCancel, busy, t }) {
  const [name, setName] = useState('')
  const [price, setPrice] = useState('')
  const [url, setUrl] = useState('')
  const [priority, setPriority] = useState(2)
  const [notes, setNotes] = useState('')

  const submit = (e) => {
    e.preventDefault()
    if (!name.trim()) return
    onSave({ name, price, url, priority, notes })
  }

  return (
    <Card>
      <CardContent className="p-3 sm:p-4">
        <form onSubmit={submit} className="space-y-2">
          <input
            type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder={t('wishlist.namePlaceholder')} maxLength={120}
            className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm" dir="auto"
          />
          <div className="flex gap-2">
            <input
              type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)}
              placeholder={t('wishlist.pricePlaceholder')}
              className="h-10 flex-1 px-3 rounded-md border border-input bg-background text-foreground text-sm" dir="ltr"
            />
            <div className="flex gap-1">
              {[1, 2, 3].map((p) => (
                <button
                  key={p} type="button" onClick={() => setPriority(p)}
                  className={cn(
                    'px-2.5 rounded-md text-[11px] font-semibold transition-colors',
                    priority === p ? 'bg-app-primary-500 text-white' : 'bg-muted text-muted-foreground',
                  )}
                  dir="auto"
                >
                  {t(`wishlist.priority.${p}`)}
                </button>
              ))}
            </div>
          </div>
          <input
            type="url" value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder={t('wishlist.urlPlaceholder')}
            className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm" dir="ltr"
          />
          <textarea
            value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder={t('wishlist.notesPlaceholder')} rows={2} maxLength={300}
            className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm resize-none" dir="auto"
          />
          <div className="flex gap-2">
            <Button type="submit" disabled={busy || !name.trim()} className="flex-1">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save')}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export default WishlistPage
